import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const createState = () => ({
    establishmentusers: [],
    establishmentusers_filters: { offset: 0, limit: 20 },
    establishmentusers_count: 0,
    establishmentusers_loaded: false,
    establishmentusers_loading: false,
    establishmentusers_loading_errors: null,

    establishmentuser_deleting: false,
    establishmentuser_deleting_errors: null,

    establishmentuser_saving: false,
    establishmentuser_saving_errors: null,

    available_users: [],
    available_users_loading: false,
    available_users_loading_errors: null,
});

const mutations = {
    updateEstablishmentUsers(state, establishmentusers) {
        state.establishmentusers = establishmentusers;
        state.establishmentusers_loaded = true;
    },
    updateEstablishmentUsersCount(state, count) {
        state.establishmentusers_count = count;
    },
    updateEstablishmentUsersFilters(state, filters) {
        state.establishmentusers_filters = filters;
    },
    updateEstablishmentUsersLoading(state, loading) {
        state.establishmentusers_loading = loading;
    },
    updateEstablishmentUsersLoadingErrors(state, errors) {
        state.establishmentusers_loading_errors = errors;
    },

    updateEstablishmentUserDeleting(state, deleting) {
        state.establishmentuser_deleting = deleting;
    },
    updateEstablishmentUserDeletingErrors(state, errors) {
        state.establishmentuser_deleting_errors = errors;
    },
    updateEstablishmentUsersLoaded(state, loaded) {
        state.establishmentusers_loaded = loaded;
    },

    updateEstablishmentUserSaving(state, saving) {
        state.establishmentuser_saving = saving;
    },
    updateEstablishmentUserSavingErrors(state, errors) {
        state.establishmentuser_saving_errors = errors;
    },
    updateAvailableUsers(state, users) {
        state.available_users = users;
    },
    updateAvailableUsersLoading(state, loading) {
        state.available_users_loading = loading;
    },
    updateAvailableUsersLoadingErrors(state, errors) {
        state.available_users_loading_errors = errors;
    },
};

const actions = {
    async deleteEstablishmentUser({ commit, dispatch, state }, params) {
        const url = `/api/establishmentusers/${params.instance.id}/`;

        commit("updateEstablishmentUserDeleting", true);
        commit("updateEstablishmentUserDeletingErrors", null);

        try {
            const response = await axios.delete(url);
            commit("updateEstablishmentUserDeleting", false);
            dispatch("fetchEstablishmentUsers");
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateEstablishmentUserDeleting", false);
            commit("updateEstablishmentUserDeletingErrors", error.details);
            throw error;
        }
    },

    async fetchEstablishmentUsers({ commit, dispatch, state, rootGetters }, params) {
        if (rootGetters['session/current_user_permissions'].indexOf("core.view_establishmentuser") === -1) {
            return;
        }
        commit("updateEstablishmentUsersLoading", true);
        commit("updateEstablishmentUsersLoadingErrors", null);

        try {
            const response = await axios.get("/api/establishmentusers/", {
                params: state.establishmentusers_filters,
            });
            commit("updateEstablishmentUsersLoading", false);
            commit("updateEstablishmentUsers", response.data.results);
            commit("updateEstablishmentUsersCount", response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateEstablishmentUsersLoadingErrors", error.details);
            commit("updateEstablishmentUsersLoading", false);
            throw error;
        }
    },

    async saveEstablishmentUser({ commit, dispatch, state, rootGetters }, params) {
        if (params.instance.id && rootGetters['session/current_user_permissions'].indexOf("core.change_establishmentuser") === -1) {
            return;
        }
        if (!params.instance.id && rootGetters['session/current_user_permissions'].indexOf("core.add_establishmentuser") === -1) {
            return;
        }

        let url = "/api/establishmentusers/";
        let method = axios.post;

        if (params.instance.id) {
            url = `/api/establishmentusers/${params.instance.id}/`;
            method = axios.put;
        }

        commit("updateEstablishmentUserSaving", true);
        commit("updateEstablishmentUserSavingErrors", null);

        try {
            const response = await method(url, params.instance);
            commit("updateEstablishmentUserSaving", false);
            dispatch("fetchEstablishmentUsers");
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updateEstablishmentUserSavingErrors", error.details);
            commit("updateEstablishmentUserSaving", false);
            throw error;
        }
    },

    init({ commit, dispatch, state }, params) {
        commit("updateEstablishmentUsersLoaded", false);
        commit("updateEstablishmentUsers", []);
        commit("updateEstablishmentUsersCount", 0);
        commit("updateEstablishmentUsersFilters", {
            ...{ offset: 0, limit: 20 },
            ...(params?.filters || {}),
        });
    },

    async initModal({ commit, dispatch, state, rootGetters }, params) {
        commit("updateAvailableUsersLoading", true);
        commit("updateAvailableUsersLoadingErrors", null);
        
        try {
            const entityId = rootGetters['session/current_entity'].id;
            
            // Fetch users for the entity
            const usersResponse = await axios.get('/api/users/', {
                params: { entity: entityId },
            });
            
            let availableUsers = usersResponse.data.results || [];
            
            // If editing an existing assignment, exclude users already assigned to this establishment
            // except the current user being edited
            if (params?.establishment && params?.exclude_assigned_users && !params?.current_user_id) {
                const assignedResponse = await axios.get('/api/establishmentusers/', {
                    params: { establishment: params.establishment },
                });
                const assignedUserIds = (assignedResponse.data.results || [])
                    .map(eu => eu.user);
                availableUsers = availableUsers.filter(u => !assignedUserIds.includes(u.id));
            } else if (params?.establishment && params?.exclude_assigned_users && params?.current_user_id) {
                // When editing, exclude other assigned users but keep the current one
                const assignedResponse = await axios.get('/api/establishmentusers/', {
                    params: { establishment: params.establishment },
                });
                const assignedUserIds = (assignedResponse.data.results || [])
                    .filter(eu => eu.id !== params.current_user_id)
                    .map(eu => eu.user);
                availableUsers = availableUsers.filter(u => !assignedUserIds.includes(u.id) || u.id === params.current_user_id);
            }
            
            commit("updateAvailableUsers", availableUsers);
            commit("updateAvailableUsersLoading", false);
            return availableUsers;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateAvailableUsersLoadingErrors", error.details);
            commit("updateAvailableUsersLoading", false);
            throw error;
        }
    },
};

export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    actions,
});

